import { useEffect, useState } from "react";
import { Brain, Sparkles, Pencil, Check, X, ShieldCheck, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Scroller, Spinner, goldBtn, ghostBtn } from "@/pages/_shared";

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/8">
      <div className="h-full rounded-full bg-gradient-to-r from-gold/50 to-gold" style={{ width: `${pct}%` }} />
    </div>
  );
}

function FactRow({ fact, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(fact.value);
  const save = async () => { await onSave(fact.key, val); setEditing(false); };
  return (
    <div className="rounded-xl border border-white/8 bg-graphite p-4" data-testid={`fact-${fact.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wider text-white/40">{fact.label}</p>
            {fact.inferred && <span className="rounded-full border border-gold/25 px-1.5 py-0.5 text-[9px] uppercase text-gold/80">learned</span>}
          </div>
          {editing ? (
            <div className="mt-2 flex gap-2">
              <input value={val} onChange={(e) => setVal(e.target.value)} className="flex-1 rounded-lg border border-gold/30 bg-black px-3 py-1.5 text-sm text-white focus:outline-none" data-testid={`fact-input-${fact.key}`} />
              <button onClick={save} className="rounded-lg bg-gold px-2.5 text-black" data-testid={`fact-save-${fact.key}`}><Check className="h-4 w-4" /></button>
              <button onClick={() => { setEditing(false); setVal(fact.value); }} className="rounded-lg border border-white/10 px-2.5 text-white/60"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <p className="mt-1 text-sm text-white/85">{fact.value}</p>
          )}
          <ConfidenceBar value={fact.confidence} />
          <p className="mt-1.5 text-[11px] text-white/30">
            {fact.source === "explicit_correction" ? "You told me this" : fact.source === "onboarding" ? "From your setup" : fact.source === "derived" ? "From your company setup" : "Learned from how you work"}
            {" · "}{Math.round((fact.confidence || 0) * 100)}% confident
            {fact.evidence_count > 1 ? ` · seen ${fact.evidence_count}×` : ""}
          </p>
        </div>
        {fact.editable && !editing && (
          <button onClick={() => setEditing(true)} className="text-white/40 hover:text-gold" data-testid={`fact-edit-${fact.key}`}><Pencil className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );
}

function OnboardingCard({ state, onDone }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const qs = state.next_questions || [];
  if (!qs.length) return null;
  const submit = async () => {
    setSaving(true);
    try { await api.post("/me/onboarding", { answers }); await onDone(); setAnswers({}); }
    finally { setSaving(false); }
  };
  return (
    <div className="mb-6 rounded-2xl border border-gold/25 bg-gradient-to-br from-gold/[0.06] to-transparent p-6" data-testid="onboarding-card">
      <div className="flex items-center gap-2.5"><Sparkles className="h-5 w-5 text-gold" />
        <h3 className="font-heading text-lg font-semibold">Help BDVG AI learn how you work</h3></div>
      <p className="mt-1 text-sm text-white/50">A few quick questions — {state.answered_count}/{state.total} done. I'll keep learning as you use the platform, so you can skip any of these.</p>
      <div className="mt-5 space-y-4">
        {qs.map((q) => (
          <div key={q.key} data-testid={`onboard-q-${q.key}`}>
            <label className="text-sm text-white/80">{q.prompt}</label>
            {q.type === "choice" ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {q.options.map((o) => (
                  <button key={o} onClick={() => setAnswers((a) => ({ ...a, [q.key]: o }))}
                    className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${answers[q.key] === o ? "border-gold bg-gold/15 text-gold" : "border-white/12 text-white/60 hover:border-gold/30"}`}
                    data-testid={`onboard-opt-${q.key}-${o.slice(0, 6)}`}>{o}</button>
                ))}
              </div>
            ) : (
              <input value={answers[q.key] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-black px-3.5 py-2.5 text-sm focus:border-gold/40 focus:outline-none"
                placeholder="Type your answer…" data-testid={`onboard-input-${q.key}`} />
            )}
          </div>
        ))}
      </div>
      <button onClick={submit} disabled={saving || !Object.keys(answers).length} className={`${goldBtn} mt-5 disabled:opacity-40`} data-testid="onboard-submit">
        {saving ? "Saving…" : "Save & continue"}
      </button>
    </div>
  );
}

export default function WhatWeKnow() {
  const [data, setData] = useState(null);
  const load = async () => { const { data } = await api.get("/me/profile"); setData(data); };
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;

  const save = async (key, value) => { await api.patch("/me/profile", { key, value }); await load(); };
  const declared = data.facts.filter((f) => !f.inferred);
  const learned = data.facts.filter((f) => f.inferred);

  return (
    <Scroller>
      <PageHeader title="What BDVG AI knows about me" icon={Brain}
        subtitle="Your private, personal intelligence profile — only you can see and edit this." />

      <div className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        {!data.onboarding.complete && <OnboardingCard state={data.onboarding} onDone={load} />}

        <div className="mb-3 flex items-center gap-2 text-xs text-white/40" data-testid="privacy-note">
          <ShieldCheck className="h-4 w-4 text-emerald-300/70" />
          This is private to you. It is never shared with other people, companies or customers, and never contains passwords or secrets.
        </div>

        {data.facts.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-charcoal p-8 text-center">
            <Lightbulb className="mx-auto h-8 w-8 text-gold/60" />
            <p className="mt-3 text-sm text-white/60">BDVG AI hasn't learned anything about you yet. Answer the questions above or just start using the platform.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {declared.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs uppercase tracking-wider text-white/35">Your preferences</h4>
                <div className="grid gap-3 sm:grid-cols-2">{declared.map((f) => <FactRow key={f.key} fact={f} onSave={save} />)}</div>
              </div>
            )}
            {learned.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs uppercase tracking-wider text-white/35">Learned from how you work</h4>
                <div className="grid gap-3 sm:grid-cols-2">{learned.map((f) => <FactRow key={f.key} fact={f} onSave={save} />)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </Scroller>
  );
}
